/**
 * Campaign Configuration
 * 
 * Centralized configuration for marketing campaigns including
 * Black Friday Cyber Monday and other promotional campaigns.
 */

export interface Campaign {
  id: string;
  name: string;
  couponId: string;
  startDate: Date;
  endDate: Date;
  discount: {
    type: 'percentage' | 'fixed';
    amount: number;
    duration: 'once' | 'repeating' | 'forever';
  };
  pricing: {
    original: number;
    discounted: number;
    currency: string;
  };
  eligibility: {
    trialUsersOnly: boolean;
    newUsersOnly: boolean;
    existingCustomers: boolean;
  };
  metadata: {
    campaign: string;
    source?: string;
  };
}

// Black Friday Cyber Monday 2025 Campaign
export const BFCM_2025_CAMPAIGN: Campaign = {
  id: 'bfcm-2025',
  name: 'Black Friday Cyber Monday 2025',
  couponId: 'BFCM2025',
  startDate: new Date('2025-11-24T00:00:00-08:00'), // Nov 24, 2025 12:00 AM PST
  endDate: new Date('2025-12-05T23:59:59-08:00'), // Dec 5, 2025 11:59 PM PST
  discount: {
    type: 'percentage',
    amount: 50,
    duration: 'once',
  },
  pricing: {
    original: 99,
    discounted: 49.50,
    currency: 'USD',
  },
  eligibility: {
    trialUsersOnly: true,
    newUsersOnly: false,
    existingCustomers: false,
  },
  metadata: {
    campaign: 'bfcm_2025',
    source: 'landing_page',
  },
};

/**
 * Check if a campaign is currently active
 */
export function isCampaignActive(campaign: Campaign): boolean {
  const now = new Date();
  return now >= campaign.startDate && now <= campaign.endDate;
}

/**
 * Get days remaining in campaign
 */
export function getCampaignDaysRemaining(campaign: Campaign): number {
  const now = new Date();
  if (now > campaign.endDate) return 0;
  if (now < campaign.startDate) return -1;
  
  const diffTime = campaign.endDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get hours remaining in campaign
 */
export function getCampaignHoursRemaining(campaign: Campaign): number {
  const now = new Date();
  if (now > campaign.endDate) return 0;
  if (now < campaign.startDate) return -1;
  
  const diffTime = campaign.endDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60));
}

/**
 * Format campaign countdown for display
 */
export function formatCampaignCountdown(campaign: Campaign): string {
  const daysRemaining = getCampaignDaysRemaining(campaign);
  const hoursRemaining = getCampaignHoursRemaining(campaign);
  
  if (daysRemaining === 0 && hoursRemaining === 0) {
    return 'Campaign ended';
  }
  
  if (daysRemaining === -1) {
    return 'Campaign not started';
  }
  
  if (daysRemaining > 1) {
    return `${daysRemaining} days remaining`;
  }
  
  if (daysRemaining === 1) {
    return '1 day remaining';
  }
  
  if (hoursRemaining > 1) {
    return `${hoursRemaining} hours remaining`;
  }
  
  return 'Less than 1 hour remaining';
}

/**
 * Get all active campaigns
 */
export function getActiveCampaigns(): Campaign[] {
  const allCampaigns = [BFCM_2025_CAMPAIGN];
  return allCampaigns.filter(isCampaignActive);
}

